import type { RiftboundCard } from '../types';
import { isOrganizedPlayPromoRune } from '../../shared/organizedPlayRune.js';
import { isOvernumberedCard as collectorPastSetSize } from '../../shared/overnumbered.js';

/** Promo catalog set codes (organized play / product / judge). */
export const PROMO_SET_CODES = ['JDG', 'OPP', 'PR'] as const;
export type PromoSetCode = (typeof PROMO_SET_CODES)[number];

/** Print-variant buckets used for set completion (standard / alt art / overnumbered). */
export type CardPrintVariant = 'standard' | 'altArt' | 'overnumbered';

/**
 * Prize badge on a promo printing. Distinct from the Champion unit type and
 * from finish suffixes like (Metal). A card has at most one: Champion wins
 * over Top 8, then Regional, then Summoner Circle.
 */
export type CardPrizeStamp = 'champion' | 'top8' | 'summoner' | 'regional';

export const CARD_PRIZE_STAMP_LABELS: Record<CardPrizeStamp, string> = {
  champion: 'Champion stamp',
  top8: 'Top 8',
  summoner: 'Summoner Circle',
  regional: 'Regional',
};

/** Search sidebar Variant filter values (Piltover-style, plus prize stamps). */
export type CardSearchVariant =
  | 'standard'
  | 'altArt'
  | 'overnumbered'
  | 'signed'
  | 'promo'
  | 'stampChampion'
  | 'stampTop8'
  | 'stampSummoner'
  | 'stampRegional';

export const CARD_SEARCH_VARIANTS: { value: CardSearchVariant; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'altArt', label: 'Alt Art' },
  { value: 'overnumbered', label: 'Overnumbered' },
  { value: 'signed', label: 'Signed' },
  { value: 'promo', label: 'Promo' },
  { value: 'stampChampion', label: 'Champion stamp' },
  { value: 'stampTop8', label: 'Top 8' },
  { value: 'stampSummoner', label: 'Summoner Circle' },
  { value: 'stampRegional', label: 'Regional' },
];

function cardPrintSuffixMatches(card: Pick<RiftboundCard, 'name'>, suffix: string): boolean {
  return new RegExp(`\\(\\s*${suffix}\\s*\\)\\s*$`, 'i').test(card.name ?? '');
}

/** Official star/signed collector codes: `VEN-195*`, `ven-195-star-166`, `195s`. */
function isSignaturePrintCode(
  card: Pick<RiftboundCard, 'number' | 'riftboundId'>,
): boolean {
  const haystack = [card.number, card.riftboundId].filter(Boolean).join(' ');
  return (
    /[*★]/.test(haystack) ||
    /(?:^|[-_])star(?:[-_]|$)/i.test(haystack) ||
    /(?:^|-)(\d+)s(?:-|$)/i.test(haystack)
  );
}

export function isPromoSetCode(setCode: string | undefined | null): boolean {
  if (!setCode) return false;
  const u = setCode.toUpperCase();
  return (PROMO_SET_CODES as readonly string[]).includes(u);
}

/** True when the card is a promo catalog printing (OPP / PR / JDG). */
export function isPromoCard(card: Pick<RiftboundCard, 'setCode'>): boolean {
  return isPromoSetCode(card.setCode);
}

/**
 * Parent product set for a promo (OGN, SFD, VEN, …), stamped at import as
 * `originSet` or recoverable from OpenRift raw metadata.
 */
export function cardOriginSet(
  card: Pick<RiftboundCard, 'originSet' | 'raw' | 'setCode'>,
): string | undefined {
  const direct = String(card.originSet || '').trim().toUpperCase();
  if (direct) return direct;
  const raw = card.raw as Record<string, unknown> | undefined;
  const openrift = raw?.openrift as Record<string, unknown> | undefined;
  if (openrift?.originSet) {
    const fromOr = String(openrift.originSet).trim().toUpperCase();
    if (fromOr) return fromOr;
  }
  const short = String(openrift?.shortCode || '').trim();
  const pub = String(openrift?.publicCode || '').trim();
  for (const candidate of [short, pub]) {
    const m = candidate.match(/^([A-Z0-9]+)-/i);
    if (m) return m[1].toUpperCase();
  }
  return undefined;
}

/** True when the print is an alternate-art treatment. */
export function isAlternateArtCard(
  card: Pick<RiftboundCard, 'name' | 'alternateArt'>,
): boolean {
  if (card.alternateArt) return true;
  return cardPrintSuffixMatches(card, 'Alternate Art');
}

/** True for signed/star legend prints — not playable Signature-supertype cards. */
export function isSignedCard(
  card: Pick<RiftboundCard, 'name' | 'number' | 'riftboundId' | 'signature' | 'supertype' | 'raw'>,
): boolean {
  // Signature spells/units (Public Execution, Tibbers) are a card type, not a print.
  if ((card.supertype ?? '').toLowerCase() === 'signature') return false;
  if (card.signature) return true;
  if (cardPrintSuffixMatches(card, 'Signed')) return true;
  if (cardPrintSuffixMatches(card, 'Signature')) return true;
  if (isSignaturePrintCode(card)) return true;
  const raw = card.raw as Record<string, unknown> | undefined;
  const openrift = raw?.openrift as Record<string, unknown> | undefined;
  const markers = openrift?.markers;
  if (Array.isArray(markers) && markers.some((m) => String(m).toLowerCase() === 'signed')) {
    return true;
  }
  return false;
}

/**
 * True when the collector number is past the printed set size (`167/166`).
 * Signed prints are included: their numbers are past the set size too.
 */
export function isOvernumberedCard(
  card: Pick<RiftboundCard, 'number' | 'riftboundId'> & Partial<Pick<RiftboundCard, 'setCode' | 'name'>>,
): boolean {
  return collectorPastSetSize(card);
}

export function isStandardSearchVariant(
  card: Pick<
    RiftboundCard,
    'name' | 'number' | 'riftboundId' | 'setCode' | 'cardType' | 'signature' | 'supertype' | 'alternateArt' | 'raw'
  >,
): boolean {
  return (
    !isPromoCard(card) &&
    !isOrganizedPlayPromoRune(card) &&
    !isAlternateArtCard(card) &&
    !isOvernumberedCard(card) &&
    !isSignedCard(card)
  );
}

/** Classify a print into the master-set variant buckets (overnumbered wins over alt art). */
export function cardPrintVariant(
  card: Pick<RiftboundCard, 'name' | 'number' | 'riftboundId'> & Partial<Pick<RiftboundCard, 'setCode'>>,
): CardPrintVariant {
  if (isOvernumberedCard(card)) return 'overnumbered';
  if (isAlternateArtCard(card)) return 'altArt';
  return 'standard';
}

function openriftMarkerSlugs(card: Pick<RiftboundCard, 'raw'>): string[] {
  const raw = card.raw as Record<string, unknown> | undefined;
  const openrift = raw?.openrift as Record<string, unknown> | undefined;
  const markers = openrift?.markers;
  if (!Array.isArray(markers)) return [];
  return markers.map((marker) => String(marker).toLowerCase());
}

function nameStamp(name: string | undefined, suffix: string): boolean {
  return new RegExp(`\\(\\s*${suffix}\\s*\\)\\s*$`, 'i').test(name ?? '');
}

/**
 * Placement stamp for a promo prize. Reads the name suffix we already append,
 * the `-champion` id, or OpenRift markers. Never the Champion unit type.
 */
export function cardPrizeStamp(
  card: Pick<RiftboundCard, 'name' | 'riftboundId' | 'raw'>,
): CardPrizeStamp | null {
  const markers = openriftMarkerSlugs(card);
  const id = String(card.riftboundId || '').toLowerCase();
  if (nameStamp(card.name, 'Champion') || /-champion$/.test(id) || markers.includes('champion')) {
    return 'champion';
  }
  if (nameStamp(card.name, 'Top 8') || markers.includes('top-8') || markers.includes('top8')) {
    return 'top8';
  }
  if (
    /\([^)]*regional[^)]*\)\s*$/i.test(card.name ?? '') ||
    markers.some((marker) => marker === 'regional' || marker === 'regional-circle' || marker === 'regional-championship')
  ) {
    return 'regional';
  }
  if (nameStamp(card.name, 'Summoner Circle') || markers.includes('summoner')) {
    return 'summoner';
  }
  return null;
}

/** Whether the card matches any selected search Variant chip (OR). */
export function cardMatchesSearchVariants(
  card: Pick<
    RiftboundCard,
    'name' | 'number' | 'riftboundId' | 'setCode' | 'cardType' | 'signature' | 'supertype' | 'alternateArt' | 'raw'
  >,
  variants: string[],
): boolean {
  if (!variants.length) return true;
  for (const raw of variants) {
    const v = String(raw);
    if (v === 'promo' && (isPromoCard(card) || isOrganizedPlayPromoRune(card))) return true;
    if (v === 'stampChampion' && cardPrizeStamp(card) === 'champion') return true;
    if (v === 'stampTop8' && cardPrizeStamp(card) === 'top8') return true;
    if (v === 'stampSummoner' && cardPrizeStamp(card) === 'summoner') return true;
    if (v === 'stampRegional' && cardPrizeStamp(card) === 'regional') return true;
    if (v === 'signed' && isSignedCard(card)) return true;
    if (v === 'altArt' && isAlternateArtCard(card)) return true;
    if (v === 'overnumbered' && isOvernumberedCard(card)) return true;
    if (v === 'standard' && isStandardSearchVariant(card)) return true;
  }
  return false;
}

/**
 * Set filter with origin-set expansion: main set codes also match promo rows
 * whose `originSet` equals that code.
 */
export function cardMatchesSetFilter(
  card: Pick<RiftboundCard, 'setCode' | 'originSet' | 'raw' | 'cardType' | 'number' | 'riftboundId'>,
  selectedSets: string[],
): boolean {
  if (!selectedSets.length) return true;
  const selected = new Set(selectedSets.map((s) => s.toUpperCase()));
  const setCode = String(card.setCode || '').toUpperCase();
  if (selected.has(setCode)) return true;
  if (selected.has('OPP') && isOrganizedPlayPromoRune(card)) return true;
  if (!isPromoCard(card)) return false;
  const origin = cardOriginSet(card);
  return Boolean(origin && selected.has(origin));
}
